/**
 * ReprojectionDialog - Layer reprojection tool UI
 *
 * Allows users to select a target CRS and reproject a layer's features
 * to a new coordinate reference system, creating a new layer with
 * reprojected features while preserving all attributes.
 *
 * Validates: Requirements 45.1, 45.2, 45.3, 45.4, 45.5
 */
import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TransformIcon from '@mui/icons-material/Transform';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  closeReprojectionDialog,
  setReprojectionTargetCRS,
  setIsReprojecting,
  setCoordinateSystemError,
} from '../store/slices/coordinateSystemSlice';
import { addLayer } from '../store/slices/layersSlice';
import {
  getCommonCRS,
  reprojectLayer,
  getCRSInfo,
  type CRSInfo,
} from '../services/CoordinateSystemManager';

const ReprojectionDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    reprojectionDialogOpen,
    reprojectionLayerId,
    reprojectionTargetCRS,
    isReprojecting,
    error,
  } = useAppSelector((state) => state.coordinateSystem);

  const layers = useAppSelector((state) => state.layers.layers);
  const [localError, setLocalError] = useState<string | null>(null);

  const selectedLayer = useMemo(
    () => layers.find((l) => l.id === reprojectionLayerId),
    [layers, reprojectionLayerId]
  );

  const commonCRS: CRSInfo[] = useMemo(() => getCommonCRS(), []);

  const sourceCRS = selectedLayer?.crs || 'EPSG:3857';
  const sourceCRSInfo = getCRSInfo(sourceCRS);

  const handleClose = () => {
    dispatch(closeReprojectionDialog());
    setLocalError(null);
  };

  const handleTargetCRSChange = (value: string) => {
    dispatch(setReprojectionTargetCRS(value));
    setLocalError(null);
  };

  const handleReproject = () => {
    if (!selectedLayer) {
      setLocalError('No layer selected for reprojection.');
      return;
    }

    if (sourceCRS === reprojectionTargetCRS) {
      setLocalError('Source and target CRS are the same. No reprojection needed.');
      return;
    }

    if (!selectedLayer.features || selectedLayer.features.length === 0) {
      setLocalError('Selected layer has no features to reproject.');
      return;
    }

    dispatch(setIsReprojecting(true));
    setLocalError(null);

    try {
      const features = selectedLayer.features.map((f) => ({
        id: `${f.id}_reproj`,
        geometry: f.geometry,
        properties: { ...f.properties },
      }));

      const result = reprojectLayer(
        features,
        sourceCRS,
        reprojectionTargetCRS,
        selectedLayer.name,
        selectedLayer.geometryType || 'Point'
      );

      // Add the new reprojected layer to the store
      dispatch(
        addLayer({
          id: `${selectedLayer.id}_${Date.now()}`,
          name: result.name,
          type: 'vector',
          geometryType: selectedLayer.geometryType,
          visible: true,
          opacity: 1,
          zIndex: layers.length,
          featureCount: result.features.length,
          features: result.features.map((f) => ({
            id: f.id,
            layerId: `${selectedLayer.id}_${Date.now()}`,
            geometry: f.geometry,
            properties: f.properties,
          })),
          crs: result.crs,
        })
      );

      dispatch(setIsReprojecting(false));
      handleClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Reprojection failed';
      setLocalError(message);
      dispatch(setCoordinateSystemError(message));
      dispatch(setIsReprojecting(false));
    }
  };

  return (
    <Dialog
      open={reprojectionDialogOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="reprojection-dialog"
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TransformIcon color="primary" />
          <Typography variant="h6">Reproject Layer</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          {/* Layer info */}
          {selectedLayer && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Layer
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {selectedLayer.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedLayer.featureCount || selectedLayer.features?.length || 0} features ·{' '}
                {selectedLayer.geometryType}
              </Typography>
            </Box>
          )}

          {/* Source CRS */}
          <Box>
            <Typography variant="caption" color="text.secondary">
              Source CRS
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Chip label={sourceCRS} size="small" variant="outlined" />
              {sourceCRSInfo && (
                <Typography variant="body2" color="text.secondary">
                  {sourceCRSInfo.name}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Target CRS selector */}
          <FormControl fullWidth>
            <InputLabel id="target-crs-label">Target CRS</InputLabel>
            <Select
              labelId="target-crs-label"
              value={reprojectionTargetCRS}
              label="Target CRS"
              onChange={(e) => handleTargetCRSChange(e.target.value)}
              data-testid="target-crs-select"
            >
              {commonCRS.map((crs) => (
                <MenuItem
                  key={crs.epsgCode}
                  value={crs.epsgCode}
                  disabled={crs.epsgCode === sourceCRS}
                >
                  <Box>
                    <Typography variant="body2">
                      {crs.epsgCode} - {crs.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {crs.projectionType} · {crs.units}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Info about target CRS */}
          {reprojectionTargetCRS && getCRSInfo(reprojectionTargetCRS) && (
            <Alert severity="info" variant="outlined">
              <Typography variant="body2">
                <strong>Target:</strong> {getCRSInfo(reprojectionTargetCRS)!.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Applicable area: {getCRSInfo(reprojectionTargetCRS)!.applicableArea}
              </Typography>
            </Alert>
          )}

          {/* Error display */}
          {(localError || error) && <Alert severity="error">{localError || error}</Alert>}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isReprojecting}>
          Cancel
        </Button>
        <Button
          onClick={handleReproject}
          variant="contained"
          disabled={isReprojecting || sourceCRS === reprojectionTargetCRS}
          startIcon={isReprojecting ? <CircularProgress size={16} /> : <TransformIcon />}
        >
          {isReprojecting ? 'Reprojecting...' : 'Reproject'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReprojectionDialog;
